import { useApolloClient } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactElement } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { z } from "zod";

import { MetadataStringFieldSchema } from "@stw/yup-validators";
import { MetadataFieldTypes } from "@stw/yup-validators";

import {
  MetadataStringFieldFragment,
  UpdateMetadataStringFieldMutation,
  UpdateMetadataStringFieldMutationVariables,
} from "~frontend/__generated__/graphql";
import { FieldForm } from "~frontend/components/campaignMetadata/FieldForm";
import { handlePostSubmit } from "~frontend/components/campaignMetadata/common";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import {
  UPDATE_METADATA_STRING_FIELD,
  GET_TENANT_METADATA_FIELDS,
} from "~frontend/graphql/campaignMetadata";
import useMutation from "~frontend/util/useMutation";

type UpdateStringFieldProps = {
  editField?: MetadataStringFieldFragment;
  initialName: string;
  onTypeChange: (name: string, type: MetadataFieldTypes) => void;
  onCancelClick: () => void;
};

type FormInput = FieldValues & z.input<typeof MetadataStringFieldSchema>;
type FormOutput = FieldValues & z.infer<typeof MetadataStringFieldSchema>;

const StringFieldForm = FieldForm<FormInput>;

export function EditStringFieldForm({
  editField,
  onTypeChange,
  onCancelClick,
  initialName,
}: UpdateStringFieldProps): ReactElement {
  const id = editField ? editField.id : undefined;

  const client = useApolloClient();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    getValues,
    watch,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(MetadataStringFieldSchema),
    values: {
      id,
      name: initialName,
      required: editField?.required || false,
      defaultValue: editField?.defaultStringValue || undefined,
    },
  });

  const [updateField] = useMutation<
    UpdateMetadataStringFieldMutation,
    UpdateMetadataStringFieldMutationVariables
  >(UPDATE_METADATA_STRING_FIELD, {
    refetchQueries: [GET_TENANT_METADATA_FIELDS],
    awaitRefetchQueries: true,
  });

  const onSubmit = async (values: FormOutput) => {
    const { name, required } = values;
    const defaultValue =
      values.defaultValue === "" ? undefined : values.defaultValue;
    const { errors, data } = await updateField({
      variables: {
        input: {
          id,
          name,
          required,
          defaultValue,
        },
      },
    });

    handlePostSubmit({
      onSuccess: onCancelClick,
      setError,
      apolloClient: client,
      isNewField: !editField,
      data: data?.createOrUpdateCampaignMetadataStringField,
      errors,
    });
  };

  const onFormCancelClick = () => {
    reset();
    onCancelClick();
  };

  return (
    <StringFieldForm
      editField={editField}
      getValues={getValues}
      watch={watch}
      onTypeChange={onTypeChange}
      currentFieldType={MetadataFieldTypes.STRING}
      onCancelClick={onFormCancelClick}
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit(onSubmit)}
      control={control}
    >
      <RhfTextField
        control={control}
        label="Default Value"
        name="defaultValue"
        fullWidth
      />
    </StringFieldForm>
  );
}
